import React, {
    useCallback, useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {AgGridReact} from '@ag-grid-community/react';
import '@ag-grid-community/styles/ag-grid.css';
import "@ag-grid-community/styles/ag-theme-alpine.css";
import "@ag-grid-community/styles/ag-theme-balham.css";
import '../../../component/css/agGridGraph.css';
import axios from "axios";
import {
    API_GRAPH_SERVER_SIDE,
    API_GRAPH_UPDATE,
    API_SETTINGS_BLOCKDATE
} from "../../../service/api.endpoint";
import {Loader} from '@consta/uikit/LoaderCanary';
import {
    CellDoubleClickedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    IServerSideDatasource,
    IServerSideGetRowsParams
} from "@ag-grid-community/core";
import ModalGraphNewRecord, {isBlock} from "./modal.graph.newRecord";
import {SnackCmp} from "../../../component/snack.cmp";
import {columnDefs} from "./columns.grid.graph";
import {LicenseManager} from "@ag-grid-enterprise/core";
import {ModuleRegistry} from '@ag-grid-community/core';
import {SetFilterModule} from '@ag-grid-enterprise/set-filter';
import {ServerSideRowModelModule} from '@ag-grid-enterprise/server-side-row-model';
import TooltipGraph from "./tooltip.graph";
import cookieCmp from "../../../component/cookie.cmp";
import {useQuery} from "@tanstack/react-query";
import GraphWellHistoryModal from "./modal.graph.wellHistory";
import http from "../../../service/axiosConfig";
import {getPrimaryOrgName, getStoredUsername} from "../../../service/authAccess";

ModuleRegistry.registerModules([SetFilterModule, ServerSideRowModelModule]);

const license = process.env.REACT_APP_AGGRID
if (license) {
    LicenseManager.setLicenseKey(license);
}

interface AgGridGraphProps {
    date: Date | null | 'loading';
    rowDataTransfer: any;
    setRowDataTransfer: any;
    loading: any
}

// Получение CSRF токена из cookies
const csrftoken = cookieCmp('csrftoken');

// Установка CSRF токена в заголовок запроса
axios.defaults.headers.put['X-CSRFToken'] = csrftoken;

function emptyGraphDayValue(planValue: number | null = null) {
    return {
        bsi: [
            {
                id: null,
                bsi: null,
                ppk: null,
                depth: null,
                label: null,
                lift_end: null,
                bsi_level: null,
                lift_start: null,
                lift_total: null,
                descent_end: null,
                descent_start: null,
                descent_total: null
            }
        ],
        fio: null,
        diam: null,
        fact: null,
        plan: planValue,
        depth: null,
        level: null,
        comment: null,
        equipage: null,
        interval: null,
        time_end: null,
        time_start: null
    }
}

function formatGraphDateForApi(value: Date) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const serverTextFilterFields = new Set([
    'location',
    'deposit',
    'dns',
    'bush',
    'wells.name',
    'diam',
    'bsi_level',
    'level',
    'comment',
]);

const distinctSetFilterFields = new Set(['org', 'total_plan', 'total_fact']);
const dayPlanFilterFieldRe = /^d([1-9]|[12][0-9]|3[01])\.plan$/;

function prepareServerSideColumnDefs(defs: any[], dateForApi: string | null): any[] {
    return defs.map((column: any) => {
        const nextColumn = {...column}
        delete nextColumn.pivot
        delete nextColumn.pivotIndex
        nextColumn.enablePivot = false
        if (serverTextFilterFields.has(nextColumn.field)) {
            nextColumn.filter = 'agTextColumnFilter'
            nextColumn.filterParams = {
                filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
                defaultOption: 'contains',
                maxNumConditions: 1,
            }
        }
        if (dayPlanFilterFieldRe.test(nextColumn.field)) {
            nextColumn.filter = 'agSetColumnFilter'
        }
        if (distinctSetFilterFields.has(nextColumn.field) || dayPlanFilterFieldRe.test(nextColumn.field)) {
            nextColumn.filter = 'agSetColumnFilter'
            nextColumn.filterParams = {
                values: async (params: any) => {
                    if (!dateForApi) {
                        params.success([])
                        return
                    }

                    try {
                        const response = await http.post(API_GRAPH_SERVER_SIDE, {
                            date: dateForApi,
                            company: getPrimaryOrgName(),
                            distinctField: nextColumn.field,
                            filterModel: params.api.getFilterModel?.() || {},
                        })
                        params.success(response.data.values || [])
                    } catch (error) {
                        console.log(error)
                        params.success([])
                    }
                },
                refreshValuesOnOpen: true,
            }
        }

        if (Array.isArray(column.children)) {
            nextColumn.children = prepareServerSideColumnDefs(column.children, dateForApi)
        }

        return nextColumn
    })
}

const GridGraphGraphList: React.FC<AgGridGraphProps> = ({date, setRowDataTransfer, loading}) => {
        const [isModalOpen, setIsModalOpen] = React.useState(false);
        const gridRef = useRef<any>();
        const wellFilterActiveRef = useRef(false);
        const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
        const gridStyle = useMemo(() => ({height: '85vh', width: '100%'}), []);
        const [isLoading, setIsLoading] = useState(false);
        const loadedDateRef = useRef<string | null>(null);
        const [graphParam, setGraphParam] = useState<any>()
        const [graphNode, setGraphNode] = useState<any>()
        const [dblClickParams, setDblClickParams] = useState<any>()
        const [isAlert, setIsAlert] = useState<'success' | 'alert' | undefined>(undefined)
        const [alertText, setAlertText] = useState<string>('')
        const [alertKey, setAlertKey] = useState(0)
        const [historyWell, setHistoryWell] = useState<any>(null)

        // blockDate
        const {isPending, error, data, refetch} = useQuery({
            queryKey: ['blockDate'],
            queryFn: () =>
                http.get(API_SETTINGS_BLOCKDATE).then(
                    (res) => {
                        return res.data
                    },
                ),
        })


        const sideBar = useMemo(() => {
            return {
                toolPanels: [
                    {
                        id: 'columns',
                        labelDefault: 'ВИД',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                            suppressRowGroups: false,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            suppressColumnFilter: true,
                            suppressColumnSelectAll: true,
                            suppressColumnExpandAll: true,
                        },
                    },
                ],
            };
        }, []);

        // контекстное меню
        const getContextMenuItems = useCallback((params: any) => {
            let result: any[] = [];

            if (params.node?.group || params.node?.data?.__group) {
                return result;
            }

            if (isPending) {
                return [{
                    name: 'Проверяем доступ...',
                    disabled: true,
                }]
            }
            if (error || !data) {
                return [{
                    name: 'Не удалось проверить доступ. Обновите страницу',
                    disabled: true,
                }]
            }

            // форматируем клик в дату и проверяем на блокировку период для возможности удалить запись
            let selectedDate = params.node.data['workDate'].split('-')
            selectedDate[2] = params.column.colId.split('.')[0].slice(1)
            selectedDate = `${selectedDate[0]}-${selectedDate[1]}-${selectedDate[2]}`
            const resultIsBloc = isBlock(
                new Date(selectedDate).toLocaleDateString("ru-RU", {timeZone: "Asia/Yekaterinburg"}),
                data,
                params.node.data['deposit'],
            )

            if (params.value !== null && params.value !== undefined && params.value !== 1 && !resultIsBloc) {
                result.push(
                    {
                        name: 'Удалить мероприятие',
                        action: async () => {
                            const old_val = params.node.data[params.column.colId.split('.')[0]]
                            const plainD = emptyGraphDayValue(params.value === 2 ? 1 : null)
                            const addRegistry = async () => {
                                const newGraphData = {
                                    [params.column.colId.split('.')[0]]: plainD
                                };
                                try {
                                    const queryResult = await http.put(`${API_GRAPH_UPDATE}/${params.node.data.pk}`, {
                                        data: newGraphData,
                                        old_plan: old_val,
                                        action: 'del',
                                        username: getStoredUsername(),
                                        log_data: {
                                            date: selectedDate,
                                            deposit: params.node.data['deposit'],
                                            bush: params.node.data['bush'],
                                            wells: params.node.data['wells']['name'],
                                            username: getStoredUsername(),
                                            action: 'GRAPH_DELETE',
                                            old_data: old_val
                                        }
                                    })
                                    return queryResult.data;
                                } catch (error: any) {
                                    console.error('Ошибка при обновлении записи:', error.response.data);
                                }
                            };

                            const queryResult = await addRegistry();
                            if (!queryResult) {
                                return
                            }
                            const row_node = params.node
                            console.log(queryResult)
                            const field: string | undefined = params.column.colId
                            const dayField = field?.split('.')[0]
                            if (dayField) {
                                row_node.data[dayField] = plainD
                            }
                            row_node.setDataValue(field, plainD.plan)
                            row_node.setDataValue('total_fact', queryResult.total_fact)
                            if (queryResult.action === 'action') {
                                row_node.setDataValue('level', queryResult.level)
                                row_node.setDataValue('bsi_level', queryResult.bsi_level)
                                row_node.setDataValue('comment', queryResult.comment)
                            }
                            params.api.refreshCells({
                                rowNodes: [row_node],
                                force: true,
                            });
                        },
                        icon: '<img src="/pic/cut.svg" alt="Cut"/>'
                    },
                    // 'copy',
                    // 'separator',
                    // 'chartRange',
                )
            }
            return result;
        }, [data, error, isPending]);

        // двойной клик на грид
        const onCellDoubleClicked = (params: CellDoubleClickedEvent) => {
            if (params.node.group || params.node.data?.__group) {
                return
            }
            if (params.colDef.headerName && params.colDef.headerName.length <= 2) {
                if (date && params.colDef.field) {
                    // todo надо тоже переделать как в удалении контекст
                    const newDT = new Date(date)
                    newDT.setDate(parseInt(params.colDef.field.slice(1).split('.')[0]))
                    setGraphParam({
                        date: newDT.toLocaleDateString("ru-RU", {timeZone: "Asia/Yekaterinburg"}),
                        well: {
                            id: params.node.data.wells.id,
                            label: params.node.data.wells.name,
                            ppk: params.node.data.wells.ppk
                        },
                        collName: params.colDef.field.split('.')[0]
                    })
                    setGraphNode(params.node)
                }
                setDblClickParams(params)
                newRecordModal()
            }
        }

        // действие при закрытии модального окна
        const handleModalClose = (value: any, status: 'success' | 'alert' | undefined) => {
            setIsModalOpen(false);
            setGraphNode(null)
            setGraphParam(null)
            if (status) {
                if (status === 'alert') {
                    setAlertText(value?.message || 'Ручной ввод проходки доступен только при активном ремонте БСИ, с выбранным экипажем и временем начала/окончания')
                } else {
                    setAlertText('Действие выполнено успешно!')
                }
                setIsAlert(status)
                setAlertKey((key) => key + 1)
            }
            console.log(value)
            if (value && status !== 'alert') {
                const row_node = dblClickParams.node
                const field: string | undefined = dblClickParams.colDef.field.split('.')[0]
                if (field && value?.code !== 'Escape') {
                    row_node.setDataValue(dblClickParams.colDef.field, value[field]['plan'])
                    row_node.setDataValue('total_fact', value['total_fact'])
                    value['bsi_level'] !== 'void' && row_node.setDataValue('bsi_level', value['bsi_level'])
                    value['level'] !== 'void' && row_node.setDataValue('level', value['level'])
                    value['diam'] !== 'void' && row_node.setDataValue('diam', value['diam'])
                    value['comment'] !== 'void' && row_node.setDataValue('comment', value['comment'])
                    row_node.data[field] = value[field]
                    // row_node.setDataValue(field, value[field]['bsi'])
                    dblClickParams.api.refreshCells({
                        rowNodes: [row_node],
                        force: true,
                    });
                }
                // setReturnedValue(value);
            }
        };

        // открываем модальное окно для добавления записи о проходке
        const newRecordModal = () => {
            setIsModalOpen(true)
        }

        // id строк в гриде
        const getRowId = useMemo(() => {
            return (params: any) => {
                if (params.data.__group) {
                    return `group-${params.data.__groupField}-${params.data.__groupKey}`;
                }
                return params.data.pk
            };
        }, []);

        const defaultColDef = useMemo(() => {
            return {
                // width: 120,
                sortable: true,
                resizable: true,
                tooltipComponent: TooltipGraph,
                enablePivot: false,
                // flex: 1
            };
        }, []);

        const dateForApi = useMemo(() => {
            if (!date || date === 'loading') {
                return null
            }
            return formatGraphDateForApi(date)
        }, [date]);
        const scopedCompany = useMemo(() => getPrimaryOrgName(), []);

        const serverSideColumnDefs = useMemo(() => prepareServerSideColumnDefs(columnDefs, dateForApi), [dateForApi]);

        const autoGroupColumnDef = useMemo(() => {
            return {
                width: 200,
                resizable: true,
                cellRendererParams: {
                    suppressCount: false,
                },
            };
        }, []);

        const getChildCount = useCallback((data: any) => {
            return data?.childCount || 0
        }, []);

        const syncWellFilterGroupState = useCallback((api: any) => {
            const filterModel = api.getFilterModel?.() || {}
            const wellFilterActive = Boolean(filterModel['wells.name'])
            wellFilterActiveRef.current = wellFilterActive

            if (wellFilterActive) {
                window.setTimeout(() => {
                    api.expandAll?.()
                }, 0)
            }
        }, []);

        const onFilterChanged = useCallback((params: FilterChangedEvent) => {
            syncWellFilterGroupState(params.api)
        }, [syncWellFilterGroupState]);

        const isServerSideGroupOpenByDefault = useCallback(() => {
            return wellFilterActiveRef.current
        }, []);


        const graphServerSideDatasource = useMemo<IServerSideDatasource | null>(() => {
            if (!dateForApi) {
                return null
            }

            return {
                getRows: async (params: IServerSideGetRowsParams) => {
                    try {
                        const response = await http.post(API_GRAPH_SERVER_SIDE, {
                            date: dateForApi,
                            company: scopedCompany,
                            startRow: params.request.startRow,
                            endRow: params.request.endRow,
                            sortModel: params.request.sortModel || [],
                            filterModel: params.request.filterModel || {},
                            rowGroupCols: params.request.rowGroupCols || [],
                            groupKeys: params.request.groupKeys || [],
                        });
                        params.success({
                            rowData: response.data.rows || [],
                            rowCount: response.data.lastRow,
                        });
                        setIsLoading(false);
                        loading(false);
                    } catch (error) {
                        console.log(error);
                        params.fail();
                        setIsLoading(false);
                        loading(false);
                    }
                },
            }
        }, [dateForApi, loading, scopedCompany]);

        const onGridReady = useCallback((params: GridReadyEvent) => {
            if (graphServerSideDatasource) {
                params.api.setServerSideDatasource(graphServerSideDatasource);
            }
            syncWellFilterGroupState(params.api);
        }, [graphServerSideDatasource, syncWellFilterGroupState]);

        useEffect(() => {
            if (gridRef.current?.api && graphServerSideDatasource) {
                gridRef.current.api.setServerSideDatasource(graphServerSideDatasource);
            }
        }, [graphServerSideDatasource]);

        const statusBar = useMemo(() => {
            return {
                statusPanels: [{statusPanel: 'agAggregationComponent', align: 'right'}],
            };
        }, []);

        // действия при выборе месяца
        useEffect(() => {
            // если рендер первый то выходим
            // if (firstUpdate.current) {
            //     firstUpdate.current = false;
            //     return;
            // }
            const fetchDataAndReset = async () => {
                if (dateForApi && dateForApi !== loadedDateRef.current) {
                    // инициализация state
                    setIsLoading(true);
                    loading(true)
                    setRowDataTransfer(null);

                    loadedDateRef.current = dateForApi;

                    // обновление данных после завершения fetchData
                    refetch();
                }
            };

            if (dateForApi) {
                fetchDataAndReset();
            }
        }, [dateForApi, loading, refetch, setRowDataTransfer]);


        return (

            <div style={containerStyle}>
                    <div className="example-wrapper">
                        <div style={{marginBottom: '5px'}}>
                            <span id="eMessage"></span>
                        </div>
                        {isLoading && <Loader/>}
                        {graphNode && <ModalGraphNewRecord isOpen={isModalOpen}
                                                           onClose={handleModalClose}
                                                           props={graphParam}
                                                           nodeData={graphNode}/>}
                        {historyWell && (
                            <GraphWellHistoryModal
                                row={historyWell}
                                onClose={() => setHistoryWell(null)}
                            />
                        )}
                        <div style={gridStyle} className="ag-theme-alpine">
                            <AgGridReact
                                ref={gridRef}
                                rowModelType={'serverSide'}
                                cacheBlockSize={100}
                                maxBlocksInCache={8}
                                blockLoadDebounceMillis={120}
                                suppressFocusAfterRefresh={true}
                                columnDefs={serverSideColumnDefs}
                                suppressAggFuncInHeader={true}
                                animateRows={true}
                                rowGroupPanelShow={'always'}
                                pivotPanelShow={'never'}
                                getRowId={getRowId}
                                getChildCount={getChildCount}
                                isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
                                defaultColDef={defaultColDef}
                                autoGroupColumnDef={autoGroupColumnDef}
                                sideBar={sideBar}
                                statusBar={statusBar}
                                onGridReady={onGridReady}
                                onFilterChanged={onFilterChanged}
                                columnHoverHighlight={true}
                                onCellDoubleClicked={onCellDoubleClicked}
                                enableRangeSelection={true}
                                allowContextMenuWithControlKey={true}
                                getContextMenuItems={getContextMenuItems}
                                tooltipShowDelay={0}
                                tooltipHideDelay={6000}
                                context={{
                                    openWellHistory: setHistoryWell,
                                }}
                            />

                        </div>
                        {isAlert && <SnackCmp key={alertKey} alertType={isAlert} alertText={alertText}/>}
                    </div>
            </div>
        );
    }
;

export default GridGraphGraphList;
